<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="bs-question">
        <span class="bs-bar bs-bar--xl"></span>
      </div>
      <div class="bs-board">
        <div
          v-for="(n, i) in notes"
          :key="n.text"
          class="bs-note"
          :class="{ '--landing': i === 0 }"
          :style="{ '--note-i': i, '--rot': n.rot + 'deg', '--x': n.x + '%', '--y': n.y + '%' }"
        >
          <span class="bs-note-text">{{ n.text }}</span>
          <div class="bs-votes">
            <span class="material-symbols-rounded bs-vote-up">arrow_upward</span>
            <span class="bs-vote-count">{{ n.votes }}</span>
          </div>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="bs-phone-q"></span>
      <div class="bs-phone-note">
        <span class="bs-phone-text">Async-first standups<span class="bs-caret">|</span></span>
      </div>
      <button type="button" class="bs-phone-add" tabindex="-1">
        <span class="material-symbols-rounded">add</span>
        Add idea
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewBrainstorm',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      notes: [
        {
          text: 'Async standups', x: 12, y: 8, rot: -3, votes: 5,
        },
        {
          text: 'Pair coding Fri', x: 56, y: 12, rot: 2, votes: 8,
        },
        {
          text: 'Better docs', x: 28, y: 52, rot: 1, votes: 3,
        },
        {
          text: 'Demo days', x: 64, y: 56, rot: -2, votes: 6,
        },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.bs-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
}
.bs-bar--xl { width: 70%; }

.bs-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.bs-board {
  position: relative;
  flex: 1;
  min-height: 130px;
  background:
    linear-gradient(rgba(15, 15, 20, 0.04) 1px, transparent 1px) 0 0 / 18px 18px,
    rgba(15, 15, 20, 0.02);
  border-radius: 6px;
}
.bs-note {
  position: absolute;
  top: var(--y); left: var(--x);
  width: 64px; min-height: 50px;
  background: color-mix(in srgb, var(--accent) 18%, white);
  border: 1px solid color-mix(in srgb, var(--accent) 35%, transparent);
  border-radius: 4px;
  padding: 5px 6px;
  display: flex; flex-direction: column; justify-content: space-between;
  gap: 3px;
  transform: rotate(var(--rot));
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.08);
}
.bs-note-text {
  font-size: 9px; font-weight: 700;
  color: var(--accent);
  line-height: 1.2;
}
.bs-votes {
  display: flex; align-items: center; gap: 2px;
}
.bs-vote-up { font-size: 10px; color: var(--accent); }
.bs-vote-count {
  font-size: 9px; font-weight: 700; color: var(--accent);
}
.bs-note.--landing {
  animation: bs-drop 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

/* ---- Audience phone ---- */
.bs-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.bs-phone-note {
  background: color-mix(in srgb, var(--accent) 18%, white);
  border: 1px solid color-mix(in srgb, var(--accent) 40%, transparent);
  border-radius: 6px;
  padding: 14px 10px;
  min-height: 56px;
  transform: rotate(-1.5deg);
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.08);
}
.bs-phone-text {
  font-size: 11px; font-weight: 700;
  color: var(--accent);
}
.bs-caret { color: var(--accent); animation: bs-blink 1s steps(2) infinite; }
.bs-phone-add {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 700; font-size: 12px;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  cursor: pointer;
  animation: bs-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.bs-phone-add .material-symbols-rounded { font-size: 14px; }

@keyframes bs-drop {
  0%, 12%  { transform: rotate(var(--rot)) translateY(-30px) scale(0.6); opacity: 0; }
  35%      { transform: rotate(var(--rot)) translateY(0) scale(1.05); opacity: 1; }
  50%, 92% { transform: rotate(var(--rot)) translateY(0) scale(1); opacity: 1; }
  100%     { transform: rotate(var(--rot)) translateY(0) scale(1); opacity: 1; }
}
@keyframes bs-blink { 0% { opacity: 1; } 100% { opacity: 0; } }
@keyframes bs-tap {
  0%, 60%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  72%       { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%      { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .bs-note.--landing { animation: none; }
  .bs-caret, .bs-phone-add { animation: none; }
}
</style>
